import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CloseIcon } from '../common/CloseIcon';
import './PriceEstimationReport.scss';

export interface EstimationData {
  estimation_id: string;
  address: {
    street?: string;
    houseNumber?: string;
    city?: string;
    postcode?: string;
    country?: string;
  };
  property_details: {
    size?: string | number;
    rooms?: string;
    floor?: string | number;
    hasElevator?: boolean;
    hasBalcony?: boolean;
    renovationStatus?: string;
    buildingType?: string;
    description?: string;
  };
  result: {
    report_text: string;
    estimated_price: number | null;
    price_range: {
      min: number | null;
      max: number | null;
    };
    confidence_level: string;
    price_per_sqm: number | null;
    market_factors: string[];
    comparable_properties: string[];
    recommendations: string[];
    sources: string[];
  };
  created_at: string;
  completed_at: string | null;
}

interface Props {
  estimation: EstimationData;
  onClose?: () => void;
  shareUrl?: string;
}

/**
 * Renders a completed price estimation: address, property details and the
 * AI-written report. The report is GitHub-flavoured markdown (the model writes
 * tables and bare URLs), so it is parsed with remark-gfm.
 */
export const PriceEstimationContent: React.FC<Props> = ({
  estimation,
  onClose,
  shareUrl
}) => {
  const { t } = useTranslation();

  // Format address for display
  const formatAddress = () => {
    const { address } = estimation;
    const parts = [];
    
    if (address.street) {
      let street = address.street;
      if (address.houseNumber) {
        street += ` ${address.houseNumber}`;
      }
      parts.push(street);
    }
    
    if (address.postcode) parts.push(address.postcode);
    if (address.city) parts.push(address.city);
    
    return parts.join(', ');
  };

/*   const handlePrint = () => {
    // Add print mode class to force full content visibility
    document.body.classList.add('printing-report');
    
    // Small delay to ensure styles are applied before printing
    setTimeout(() => {
      window.print();
      
      // Remove the class after print dialog closes
      setTimeout(() => {
        document.body.classList.remove('printing-report');
      }, 100);
    }, 100);
  }; */

  /**
   * Copies the report link to the clipboard and tells the user the outcome.
   * Used when the browser exposes no native share sheet, and as the fallback
   * when navigator.share fails for any reason other than a user cancel.
   */
  const copyReportLink = (url: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => alert(t('priceEstimation.linkCopied')))
        .catch(err => {
          console.error('Failed to copy:', err);
          alert(t('shareButton.linkCopiedError'));
        });
      return;
    }

    // Fallback for non-secure contexts or older browsers
    const textArea = document.createElement("textarea");
    textArea.value = url;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      alert(t('priceEstimation.linkCopied'));
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
      alert(t('shareButton.linkCopiedError'));
    }
    document.body.removeChild(textArea);
  };

  /**
   * Shares the price estimation report, sharing the URL on its own to match
   * every other share entry point in the app. Passing `text` alongside `url`
   * makes the macOS share sheet copy "url\ntext", which is not a pasteable
   * link. A failed share falls back to copying the link.
   */
  const handleShare = async () => {
    const url = shareUrl || window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ url });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error sharing:', err);
      }
    }

    copyReportLink(url);
  };

  return (
    <div className="price-estimation-report">
      {/* Header */}
      <div className="price-estimation-report__header">
        <div className="price-estimation-report__header-content">
          <h1 className="price-estimation-report__title">
            <Trans i18nKey="priceEstimation.title" />
          </h1>
          <p className="price-estimation-report__address">{formatAddress()}</p>
        </div>
        <div className="price-estimation-report__header-actions">
          <button 
            className="price-estimation-report__action-btn"
            onClick={handleShare}
            title={t('priceEstimation.share')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M8.59 13.51l6.83 3.98m-.01-10.98l-6.82 3.98M21 5a3 3 0 11-6 0 3 3 0 016 0zM9 12a3 3 0 11-6 0 3 3 0 016 0zm12 7a3 3 0 11-6 0 3 3 0 016 0z" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <Trans i18nKey="priceEstimation.share" />
          </button>
          {onClose && (
            <button
              className="price-estimation-report__close-btn"
              onClick={onClose}
              title={t('priceEstimation.close')}
            >
              <CloseIcon size={24} />
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="price-estimation-report__content">
        {/* Property Details Summary */}
        <div className="price-estimation-report__property-details">
          <h2>{t('priceEstimation.propertyDetails')}</h2>
          <div className="price-estimation-report__details-grid">
            {estimation.property_details.size && (
              <div className="price-estimation-report__detail-item">
                <span className="price-estimation-report__detail-label">{t('priceEstimation.size')}:</span>
                <span className="price-estimation-report__detail-value">{estimation.property_details.size} m²</span>
              </div>
            )}
            {estimation.property_details.rooms && (
              <div className="price-estimation-report__detail-item">
                <span className="price-estimation-report__detail-label">{t('priceEstimation.rooms')}:</span>
                <span className="price-estimation-report__detail-value">{estimation.property_details.rooms}</span>
              </div>
            )}
            {estimation.property_details.floor && (
              <div className="price-estimation-report__detail-item">
                <span className="price-estimation-report__detail-label">{t('priceEstimation.floor')}:</span>
                <span className="price-estimation-report__detail-value">{estimation.property_details.floor}</span>
              </div>
            )}
            <div className="price-estimation-report__detail-item">
              <span className="price-estimation-report__detail-label">{t('priceEstimation.elevator')}:</span>
              <span className="price-estimation-report__detail-value">
                {estimation.property_details.hasElevator ? t('common.yes') : t('common.no')}
              </span>
            </div>
            <div className="price-estimation-report__detail-item">
              <span className="price-estimation-report__detail-label">{t('priceEstimation.balcony')}:</span>
              <span className="price-estimation-report__detail-value">
                {estimation.property_details.hasBalcony ? t('common.yes') : t('common.no')}
              </span>
            </div>
            {estimation.property_details.renovationStatus && (
              <div className="price-estimation-report__detail-item price-estimation-report__detail-item">
                <span className="price-estimation-report__detail-label price-estimation-report__detail-label--condition">{t('priceEstimation.condition')}:</span>
                <span className="price-estimation-report__detail-value price-estimation-report__detail-value--condition">
                  {t(`building.offMarket.modal.${estimation.property_details.renovationStatus?.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}
                </span>
              </div>
            )}
            {estimation.property_details.buildingType && (
              <div className="price-estimation-report__detail-item">
                <span className="price-estimation-report__detail-label">{t('priceEstimation.buildingType')}:</span>
                <span className="price-estimation-report__detail-value">
                  {t(`building.offMarket.modal.${estimation.property_details.buildingType?.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Full Report Text */}
        <div className="price-estimation-report__full-report">
          <div className="price-estimation-report__markdown">
            {/* singleTilde off: reports write "~€540k" for "about", which GFM would strike through */}
            <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>
              {estimation.result.report_text}
            </ReactMarkdown>
          </div>
        </div>

        {/* Footer */}
        <div className="price-estimation-report__footer">
          <p className="price-estimation-report__disclaimer">
            <Trans i18nKey="priceEstimation.disclaimer" />
          </p>
          <p className="price-estimation-report__timestamp">
            {t('priceEstimation.generatedOn')}: {new Date(estimation.completed_at || estimation.created_at).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

